/**
 * Research Tour
 * Semantic search for finding legislation by concept
 *
 * This tour navigates from the nav bar to the /search page.
 * Uses sessionStorage to persist tour state across page navigation.
 */

import type { Tour } from '@/types/onboarding';
import { saveTourResumeState } from './committee-tour';

/**
 * Navigate to /search page.
 * Saves tour state so it can resume after navigation.
 */
function navigateToSearch(
  _element: Element | undefined,
  _step: unknown,
  opts: { driver: { destroy: () => void } }
) {
  // Save state to resume at step 2 (index 1) after navigation
  saveTourResumeState('research', 1);

  // Stop the current driver instance before navigation
  opts.driver.destroy();

  // Navigate to search page
  window.location.href = '/search';
}

export const researchTour: Tour = {
  id: 'research',
  name: 'Semantic Search',
  description: 'Find legislation by concept using AI-powered search',
  // No startRoute - tour begins wherever user is, highlighting nav search icon
  steps: [
    {
      element: '[data-tour="global-search"]',
      popover: {
        title: 'AI-Powered Search',
        description:
          'Access our semantic search to find bills by concept, topic, or natural language queries.',
        side: 'bottom',
        align: 'center',
        onNextClick: navigateToSearch,
      },
    },
    {
      element: '[data-tour="semantic-search-input"]',
      popover: {
        title: 'Search by meaning',
        description:
          'Type concepts like "bills protecting small businesses" or "environmental regulations" — our AI understands what you mean.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="search-suggestions"]',
      popover: {
        title: 'Try these examples',
        description:
          'Click any suggestion to see how semantic search finds relevant bills, even without exact keyword matches.',
        side: 'top',
        align: 'center',
      },
    },
  ],
};
